/**
 * imgProxy — helper for the /api/img-proxy backend route.
 *
 * Wraps image URLs in a server-side resize + WebP transcode + 1-year-immutable-
 * cache shim. Routes:
 *   - External assets on customer-assets.emergentagent.com (pre-pipeline uploads)
 *   - Local /api/files/serve uploads that kept their transparency-PNG format
 *     (the upload pipeline only re-encodes non-transparent PNGs to WebP;
 *      transparent PNGs are kept as full-size PNG and can still be huge —
 *      e.g. a hero banner at 2.1 MB).
 *
 * Usage:
 *   <img src={proxify(url, 1600)} srcSet={proxifySrcSet(url)} sizes="(max-width: 768px) 100vw, 50vw" />
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Hosts whose images should be routed through the proxy.
// Includes the production + preview storefront origins so /api/files/serve
// URLs flow through the resize pipeline too.
const PROXIED_HOSTS = [
  "customer-assets.emergentagent.com",
  "xyzplus.in",
  "www.xyzplus.in",
];

const toAbsolute = (url) => {
  if (!url || typeof url !== "string") return null;
  if (url.startsWith("data:") || url.startsWith("blob:")) return null;
  try {
    // Resolves relative /api/files/serve?path=... against the backend origin
    // (which is the storefront's public URL).
    return new URL(url, BACKEND || (typeof window !== "undefined" ? window.location.origin : "https://localhost")).toString();
  } catch {
    return null;
  }
};

const isProxyable = (url) => {
  const abs = toAbsolute(url);
  if (!abs) return false;
  try {
    const u = new URL(abs);
    // Production / external CDN: always proxify.
    if (PROXIED_HOSTS.some((h) => u.hostname === h)) return true;
    // Same-origin (preview) /api/files/serve uploads: proxify only the file-serve
    // path so we don't recurse on /api/img-proxy itself or hit other routes.
    if (typeof window !== "undefined" && u.origin === window.location.origin && u.pathname.startsWith("/api/files/serve")) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

/**
 * Return a proxy URL that delivers the image as resized WebP, or the original
 * URL unchanged if proxying isn't applicable.
 *
 * @param {string} url   absolute or storefront-relative image URL
 * @param {number} w     target width (320 / 480 / 640 / 768 / 1024 / 1280 / 1600 / 1920)
 * @param {number} q     WebP quality (default 82)
 */
export function proxify(url, w = 1600, q = 82) {
  if (!url) return url;
  if (!isProxyable(url)) return url;
  const abs = toAbsolute(url);
  const base = BACKEND || "";
  return `${base}/api/img-proxy?url=${encodeURIComponent(abs)}&w=${w}&q=${q}`;
}

/**
 * Build a responsive `srcset` so the browser picks the smallest image that
 * still fits the layout. Cuts mobile payload by ~80% for hero/banner images.
 */
export function proxifySrcSet(url, widths = [640, 1024, 1600]) {
  if (!url || !isProxyable(url)) return undefined;
  return widths.map((w) => `${proxify(url, w)} ${w}w`).join(", ");
}
